/**
 * Serves a prototype's images to the light preview (light-preview plan F2).
 *
 * With a WebContainer, Vite served `public/` at the site root, so `/media/logo.png` in the
 * generated code just worked. The light preview renders from a blob document and has no server:
 * a path-absolute URL there resolves against our own origin, and a relative one against a blob
 * URL with no directory, so every image came out broken.
 *
 * The workbench store already holds each asset's bytes as base64 — the same copy the editor and
 * the snapshot zip use — so the compiled output gets those bytes inlined as `data:` URIs. No
 * network, nothing to revoke, and the preview keeps working when the tab is offline.
 */

export interface PreviewAsset {
  base64: string;
  mimeType?: string;
}

export type PreviewAssets = Readonly<Record<string, PreviewAsset>>;

const MIME_BY_EXTENSION: Readonly<Record<string, string>> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  svg: 'image/svg+xml',
  ico: 'image/x-icon',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  woff: 'font/woff',
  woff2: 'font/woff2',
  ttf: 'font/ttf',
  pdf: 'application/pdf',
};

function mimeTypeFor(path: string, declared?: string): string {
  if (declared) return declared;
  const ext = path.slice(path.lastIndexOf('.') + 1).toLowerCase();
  return MIME_BY_EXTENSION[ext] ?? 'application/octet-stream';
}

/**
 * The URL the generated code uses for an asset. Assets are stored at their workspace path
 * (`public/media/logo.png`); Vite serves everything under `public/` from the root, which is what
 * the code references (`/media/logo.png`).
 */
export function assetReferenceOf(storePath: string): string {
  return storePath.replace(/^\/+/, '').replace(/^public\//, '');
}

export function assetDataUri(storePath: string, asset: PreviewAsset): string {
  return `data:${mimeTypeFor(storePath, asset.mimeType)};base64,${asset.base64}`;
}

/**
 * A `public/` file the workspace keeps as text. An SVG is the one that matters: snapshots treat
 * it as UTF-8, not as binary, so a brand logo lives in the file store rather than in `assets` —
 * and inlining only the binary ones left exactly one broken image on the page. Percent-encoded
 * rather than base64, which keeps it readable and needs no `btoa`.
 */
export function textAssetDataUri(storePath: string, content: string): string {
  return `data:${mimeTypeFor(storePath)};charset=utf-8,${encodeURIComponent(content)}`;
}

/**
 * Reference (`media/logo.png`) → `data:` URI, for everything the workspace serves from `public/`:
 * the binary assets, plus the text files that live in the file store.
 */
export function buildAssetUrlMap(
  assets: PreviewAssets,
  files: Readonly<Record<string, string>> = {},
): Map<string, string> {
  const urls = new Map<string, string>();
  for (const [storePath, asset] of Object.entries(assets)) {
    if (!asset?.base64) continue;
    urls.set(assetReferenceOf(storePath), assetDataUri(storePath, asset));
  }
  for (const [storePath, content] of Object.entries(files)) {
    if (!storePath.startsWith('public/') || typeof content !== 'string') continue;
    const reference = assetReferenceOf(storePath);
    // The binary copy wins: an asset written this turn is the freshest bytes there are.
    if (urls.has(reference)) continue;
    urls.set(reference, textAssetDataUri(storePath, content));
  }
  return urls;
}

const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Replaces every reference to a stored asset with its `data:` URI, in compiled JS or in CSS.
 *
 * Generated code writes these three ways — `/media/x.png` in JSX, `media/x.png` from Chef's own
 * asset refs, and `./media/x.png` from a relative import — so all three are matched. The pattern
 * requires a delimiter before the reference (a quote, `(`, whitespace or `=`) so a longer path
 * that merely ends with the same characters is left alone.
 */
export function inlineAssetReferences(source: string, urls: Map<string, string>): string {
  if (!source || urls.size === 0) return source;
  let out = source;
  for (const [reference, url] of urls) {
    const pattern = new RegExp(`(["'\`(=\\s])(?:\\.?\\/)?${escapeForRegExp(reference)}`, 'g');
    out = out.replace(pattern, (_match, delimiter: string) => `${delimiter}${url}`);
  }
  return out;
}
